import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "../components/ErrorBoundary/ErrorBoundary";
import { lazy, Suspense, useEffect } from "react";
import { LoadingSpinner } from "../components/Loading/LoadingSpinner";
import { AuthProvider, useAuth } from "../contexts/AuthContext";
import { ToastProvider } from "../contexts/ToastContext";
import { initAnalytics, identifyUser } from "./lib/analytics";
import { ingestImpersonateFromUrl } from "./lib/impersonate";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { QuickLookToolbar } from "./components/QuickLookToolbar";
import { PaxToolShell } from "./components/PaxToolShell";
import { ContentProvider } from "./lib/content";

import { PaxBuildFreshness } from "./components/PaxBuildFreshness";
import { resolvePaxRecoveryCallbackPath } from "./auth/paxRecoveryCallback";
// Run ingestImpersonateFromUrl SYNCHRONOUSLY at module load — BEFORE any
// React component renders. If we did this inside a useEffect, the
// ImpersonationBanner's useState initialiser would read an empty
// sessionStorage and miss the active session for that first render pass.
// `typeof window` guard keeps Vitest / SSR happy.
if (typeof window !== "undefined") {
  ingestImpersonateFromUrl();
  // Supabase password recovery uses its exact allowlisted Pax root and returns
  // implicit-flow credentials in the URL fragment. Move that callback to the
  // dedicated reset screen before the router renders; the reset component
  // exchanges the credentials and immediately removes them from browser history.
  const recoveryPath = resolvePaxRecoveryCallbackPath(window.location);
  if (recoveryPath) {
    window.history.replaceState(null, "", recoveryPath);
  }
}

// Lazy load pax components
const PaxLogin = lazy(() => import("./auth/PaxLogin"));
const PaxSignup = lazy(() => import("./auth/PaxSignup"));
const PaxRecoveryRequest = lazy(() => import("./auth/PaxForgotPassword"));
const PaxRecoveryUpdate = lazy(() => import("./auth/PaxResetPassword"));
const PaxDashboard = lazy(() => import("./dashboard/PaxDashboard"));
const PaxAuthGuard = lazy(() => import("./auth/PaxAuthGuard"));

// Lazy load bookings
const PaxMyBookings = lazy(() => import("./bookings/PaxMyBookings"));

// Lazy load consumer tools
const FlightSearchPage = lazy(() =>
  import("../components/PropellerTravel/FlightSearchPage").then((m) => ({
    default: m.FlightSearchPage,
  })),
);
const AlertsManagementPage = lazy(() =>
  import("../components/PropellerTravel/AlertsManagementPage").then((m) => ({
    default: m.AlertsManagementPage,
  })),
);
const FLIFOPage = lazy(() =>
  import("../components/PropellerTravel/FLIFOPage").then((m) => ({
    default: m.FLIFOPage,
  })),
);
const MCTCheckerPage = lazy(() =>
  import("../components/PropellerTravel/MCTCheckerPage").then((m) => ({
    default: m.MCTCheckerPage,
  })),
);
const PaxAvailabilityPage = lazy(() => import("./tools/PaxAvailabilityPage"));
const PaxFareQuotePage = lazy(() => import("./tools/PaxFareQuotePage"));
const PaxPriceExplorerPage = lazy(() => import("./tools/PaxPriceExplorerPage"));
const PaxSeatMapPage = lazy(() => import("./tools/PaxSeatMapPage"));
const PaxProfile = lazy(() => import("./profile/PaxProfile"));
const MagicLinkVerification = lazy(() =>
  import("../components/Auth/MagicLinkVerification").then((m) => ({
    default: m.MagicLinkVerification,
  })),
);

// Define Pax-specific routes
const paxRouter = createBrowserRouter([
  {
    path: "/login",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxLogin />
      </Suspense>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxRecoveryRequest />
      </Suspense>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxRecoveryUpdate />
      </Suspense>
    ),
  },
  {
    path: "/auth/magic",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <MagicLinkVerification />
      </Suspense>
    ),
  },
  {
    path: "/register",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxSignup />
      </Suspense>
    ),
  },
  {
    path: "/",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxDashboard />
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/bookings",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxToolShell>
            <PaxMyBookings />
          </PaxToolShell>
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/tools/flight-search",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxToolShell>
            <FlightSearchPage bypassAccessCheck />
          </PaxToolShell>
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    // Legacy alias — pax users now get the dedicated pax seat-map page,
    // not the staff SeatMapPage (which has a feature-access gate that
    // rejects magic-link users).
    path: "/tools/seat-maps",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxSeatMapPage />
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/tools/alerts",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxToolShell>
            <AlertsManagementPage bypassAccessCheck />
          </PaxToolShell>
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/tools/flifo",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxToolShell>
            <FLIFOPage bypassAccessCheck />
          </PaxToolShell>
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/tools/mct",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxToolShell>
            <MCTCheckerPage />
          </PaxToolShell>
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/tools/availability",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxAvailabilityPage />
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/tools/fare-quote",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxFareQuotePage />
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/tools/price-explorer",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxPriceExplorerPage />
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/tools/seat-map",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxSeatMapPage />
        </PaxAuthGuard>
      </Suspense>
    ),
  },
  {
    path: "/profile",
    element: (
      <Suspense fallback={<LoadingSpinner size="lg" />}>
        <PaxAuthGuard>
          <PaxProfile />
        </PaxAuthGuard>
      </Suspense>
    ),
  },
]);

// Analytics wrapper to identify users on load
function PaxAnalyticsWrapper({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  useEffect(() => {
    // ingestImpersonateFromUrl already ran at module-load time (see top
    // of this file). Just init analytics here.
    initAnalytics();
    document.title = "Propeller Travel Client Portal";
  }, []);

  useEffect(() => {
    if (user) {
      // identifyUser is a no-op during impersonation (see analytics.ts)
      // — it must not pollute the customer's PostHog profile.
      identifyUser(user.id, user.email || "", user.user_metadata?.first_name);
    }
  }, [user]);

  return <>{children}</>;
}

export default function PaxApp() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        {/*
         * ToastProvider is required because shared components
         * pulled in from the staff side (e.g. SeatMapViewer)
         * call `useToast()` for share-link / copy-to-clipboard
         * feedback. Without it, those components throw
         * "useToast must be used within ToastProvider" the
         * moment a pax user opens them.
         */}
          <PaxBuildFreshness />
        <ToastProvider>
          {/*
           * ContentProvider hydrates the pax-portal CMS
           * dictionary once per session (CDN-cached for 5
           * minutes). Components call useContent(key, fallback)
           * to resolve copy. Never blocks render — fallbacks
           * are the hard-coded strings the platform shipped
           * with, so an empty dictionary is fully functional.
           */}
          <ContentProvider locale="en">
            <PaxAnalyticsWrapper>
              <div className="pax-platform-root min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-50 font-sans">
                <ImpersonationBanner />
                <RouterProvider router={paxRouter} />
                {/* Floating staff toolbar — renders only
                                        during impersonation. Pax users never
                                        see it; staff get "Open CRM / Copy URL
                                        / New Freshdesk ticket" shortcuts. */}
                <QuickLookToolbar />
              </div>
            </PaxAnalyticsWrapper>
          </ContentProvider>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
